import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';

import WalletSelectModal from './WalletSelectModal';
import {
  discoverWallets,
  getSelectedWalletRdns,
  setSelectedWalletRdns,
  type EIP6963ProviderDetail,
} from '../utils/WalletProviders';
import { fetchAccountDetails, resetProvider } from '../utils/BlockchainAPI';
import { clearCachedAccountDetails } from '../utils/LocalStorage';
import * as Errors from '../utils/ErrorMessages';
import type { AccountDetails } from '../utils/AccountDetails';

interface WalletSelectionContextValue {
  /** Show the wallet picker. Resolves with the chosen wallet, or null if dismissed. */
  promptForWallet: () => Promise<EIP6963ProviderDetail | null>;
}

const WalletSelectionContext = createContext<WalletSelectionContextValue>({
  promptForWallet: async () => null,
});

/**
 * Hosts the one wallet picker for the whole app, so any "Connect wallet"
 * button can resolve a multi-wallet browser rather than dead-ending.
 */
export const WalletSelectionProvider = ({ children }: { children: ReactNode }) => {
  const [wallets, setWallets] = useState<EIP6963ProviderDetail[]>([]);
  const [lastUsedRdns, setLastUsedRdns] = useState<string | null>(null);
  const [show, setShow] = useState(false);
  const resolveRef = useRef<((wallet: EIP6963ProviderDetail | null) => void) | null>(null);

  const settle = useCallback((wallet: EIP6963ProviderDetail | null) => {
    setShow(false);
    const resolve = resolveRef.current;
    resolveRef.current = null;
    resolve?.(wallet);
  }, []);

  const promptForWallet = useCallback(async () => {
    setWallets(await discoverWallets());
    setLastUsedRdns(getSelectedWalletRdns());
    setShow(true);
    return new Promise<EIP6963ProviderDetail | null>((resolve) => {
      // A second prompt while one is open supersedes the first.
      resolveRef.current?.(null);
      resolveRef.current = resolve;
    });
  }, []);

  const onSelect = useCallback((wallet: EIP6963ProviderDetail) => {
    setSelectedWalletRdns(wallet.info.rdns);
    resetProvider();
    // Details cached against the previously selected wallet are now stale.
    clearCachedAccountDetails();
    settle(wallet);
  }, [settle]);

  return (
    <WalletSelectionContext.Provider value={{ promptForWallet }}>
      {children}
      <WalletSelectModal
        show={show}
        wallets={wallets}
        lastUsedRdns={lastUsedRdns}
        onSelect={onSelect}
        onCancel={() => settle(null)}
      />
    </WalletSelectionContext.Provider>
  );
};

export const useWalletSelection = () => useContext(WalletSelectionContext);

/**
 * Connect, asking which wallet to use first.
 *
 * The picker is shown on every explicit connect while disconnected, not just
 * the first one -- the remembered choice is only a default (flagged "last
 * used"), so switching wallets never means digging through a menu. Returns
 * null when the user dismisses the picker.
 */
export const useConnectWallet = () => {
  const { promptForWallet } = useWalletSelection();

  return useCallback(async (): Promise<AccountDetails | null> => {
    const available = await discoverWallets();

    if (available.length > 1) {
      const chosen = await promptForWallet();
      if (!chosen) {
        return null;
      }
      return fetchAccountDetails();
    }

    try {
      return await fetchAccountDetails();
    } catch (err: any) {
      // A wallet could have been injected late, after the check above.
      if (err?.message !== Errors.DS_WALLET_SELECTION_REQUIRED) {
        throw err;
      }
    }

    const chosen = await promptForWallet();
    return chosen ? fetchAccountDetails() : null;
  }, [promptForWallet]);
};
